import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createBlock as _createBlock } from "vue";
const _hoisted_1 = { class: "date-range-dialog" };
const _hoisted_2 = { class: "dialog-header" };
const _hoisted_3 = { class: "dialog-body" };
const _hoisted_4 = { class: "mode-toggle" };
const _hoisted_5 = {
    key: 0,
    class: "scroll-area"
};
const _hoisted_6 = { class: "year-header" };
const _hoisted_7 = { class: "month-grid" };
const _hoisted_8 = ["onClick"];
const _hoisted_9 = {
    key: 1,
    class: "scroll-area"
};
const _hoisted_10 = ["onClick"];
const _hoisted_11 = { class: "year-label" };
const _hoisted_12 = { class: "dialog-footer" };
const _hoisted_13 = {
    key: 0,
    class: "date-fields"
};
const _hoisted_14 = { class: "date-field" };
const _hoisted_15 = { class: "date-field" };
const _hoisted_16 = {
    key: 1,
    class: "year-type-toggle"
};
const _hoisted_17 = ["disabled"];
import { ref, computed, watch } from 'vue';
import { formatMonthYearShort } from '@/utils/helpers';
import { useAppState } from '@/composables/useAppState';
import { snapToSpendingCycle } from '@/components/elements/spendingAnalysisFilterState';
export default /*@__PURE__*/ _defineComponent({
    __name: 'DateRangeDialog',
    props: {
        modelValue: { type: Boolean },
        initialMode: { default: 'months' },
        initialSelection: { default: null }
    },
    emits: ["update:modelValue", "select"],
    setup(__props, { emit: __emit }) {
        const state = useAppState();
        const MONTH_NAMES = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
        const props = __props;
        const emit = __emit;
        const mode = ref(props.initialMode);
        const yearType = ref('calendar');
        const monthPickA = ref(null);
        const monthPickB = ref(null);
        const selectedYear = ref(null);
        watch(() => props.modelValue, (open) => {
            if (open) {
                mode.value = props.initialMode;
                if (props.initialSelection) {
                    if (props.initialSelection.type === 'months') {
                        monthPickA.value = props.initialSelection.startMonth ?? null;
                        monthPickB.value = props.initialSelection.endMonth ?? null;
                        mode.value = 'months';
                    }
                    else {
                        selectedYear.value = props.initialSelection.year ?? null;
                        yearType.value = props.initialSelection.type === 'taxYear' ? 'tax' : 'calendar';
                        mode.value = 'years';
                    }
                }
                else {
                    monthPickA.value = null;
                    monthPickB.value = null;
                    selectedYear.value = null;
                }
            }
        });
        const latestMonth = computed(() => {
            const now = new Date();
            let year = now.getFullYear();
            let month = now.getMonth();
            const cycleDay = state.bankInfo?.spendingCycle?.day;
            if (snapToSpendingCycle.value && cycleDay && now.getDate() >= cycleDay) {
                month += 1;
                if (month > 11) {
                    month = 0;
                    year += 1;
                }
            }
            return { year, month };
        });
        const availableYears = computed(() => {
            const startYear = new Date().getFullYear() - 3;
            const years = [];
            for (let y = latestMonth.value.year; y >= startYear; y--)
                years.push(y);
            return years;
        });
        function getMonthsForYear(year) {
            const months = [];
            const maxMonth = year === latestMonth.value.year ? latestMonth.value.month : 11;
            for (let m = maxMonth; m >= 0; m--) {
                months.push({ key: `${year}-${String(m + 1).padStart(2, '0')}`, label: MONTH_NAMES[m] });
            }
            return months;
        }
        const monthRangeStart = computed(() => {
            if (!monthPickA.value)
                return null;
            if (!monthPickB.value)
                return monthPickA.value;
            return monthPickA.value < monthPickB.value ? monthPickA.value : monthPickB.value;
        });
        const monthRangeEnd = computed(() => {
            if (!monthPickA.value)
                return null;
            if (!monthPickB.value)
                return monthPickA.value;
            return monthPickA.value > monthPickB.value ? monthPickA.value : monthPickB.value;
        });
        function isMonthInRange(key) {
            if (!monthRangeStart.value)
                return false;
            return key >= monthRangeStart.value && key <= monthRangeEnd.value;
        }
        function monthSpan(a, b) {
            const [ay, am] = a.split('-').map(Number);
            const [by, bm] = b.split('-').map(Number);
            return Math.abs((by - ay) * 12 + (bm - am)) + 1;
        }
        function toggleMonth(key) {
            if (!monthPickA.value) {
                monthPickA.value = key;
                monthPickB.value = null;
            }
            else if (!monthPickB.value) {
                if (key === monthPickA.value) {
                    monthPickB.value = null;
                }
                else if (monthSpan(monthPickA.value, key) > 12) {
                    monthPickA.value = key;
                    monthPickB.value = null;
                }
                else {
                    monthPickB.value = key;
                }
            }
            else {
                monthPickA.value = key;
                monthPickB.value = null;
            }
        }
        function clearFrom() {
            if (!monthPickB.value) {
                monthPickA.value = null;
                return;
            }
            const keep = monthPickA.value > monthPickB.value ? monthPickA.value : monthPickB.value;
            monthPickA.value = keep;
            monthPickB.value = null;
        }
        function clearTo() {
            if (!monthPickB.value)
                return;
            const keep = monthPickA.value < monthPickB.value ? monthPickA.value : monthPickB.value;
            monthPickA.value = keep;
            monthPickB.value = null;
        }
        function formatMonthLabel(key) {
            if (!key)
                return '';
            const parts = key.split('-');
            return `${MONTH_NAMES[parseInt(parts[1]) - 1]} ${parts[0]}`;
        }
        const fromLabel = computed(() => formatMonthYearShort(monthRangeStart.value));
        const toLabel = computed(() => monthPickB.value ? formatMonthYearShort(monthRangeEnd.value) : '');
        const monthSelectionLabel = computed(() => {
            if (!monthRangeStart.value)
                return '';
            const startLabel = formatMonthLabel(monthRangeStart.value);
            if (monthRangeStart.value === monthRangeEnd.value)
                return startLabel;
            return `${startLabel} - ${formatMonthLabel(monthRangeEnd.value)}`;
        });
        const yearList = computed(() => {
            const currentYear = new Date().getFullYear();
            const years = [];
            for (let y = currentYear; y >= currentYear - 13; y--) {
                if (yearType.value === 'tax') {
                    const nextShort = String(y + 1).slice(-2);
                    years.push({ key: y, label: `${y}-${nextShort}` });
                }
                else {
                    years.push({ key: y, label: String(y) });
                }
            }
            return years;
        });
        function selectYear(year) {
            selectedYear.value = selectedYear.value === year ? null : year;
        }
        const hasSelection = computed(() => {
            if (mode.value === 'months')
                return !!monthPickA.value;
            return selectedYear.value !== null;
        });
        function confirmSelection() {
            if (!hasSelection.value)
                return;
            if (mode.value === 'months') {
                emit('select', {
                    type: 'months',
                    startMonth: monthRangeStart.value,
                    endMonth: monthRangeEnd.value,
                    label: monthSelectionLabel.value
                });
            }
            else {
                const year = selectedYear.value;
                const yearEntry = yearList.value.find(y => y.key === year);
                emit('select', {
                    type: yearType.value === 'tax' ? 'taxYear' : 'calendarYear',
                    year,
                    label: yearEntry?.label ?? String(year)
                });
            }
            emit('update:modelValue', false);
        }
        return (_ctx, _cache) => {
            const _component_v_icon = _resolveComponent("v-icon");
            const _component_v_dialog = _resolveComponent("v-dialog");
            return (_openBlock(), _createBlock(_component_v_dialog, {
                "model-value": _ctx.modelValue,
                "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event) => (emit('update:modelValue', $event))),
                "max-width": "560",
                scrim: "rgba(0, 0, 0, 0.5)",
                opacity: 0.5
            }, {
                default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_1, [
                        _createElementVNode("div", _hoisted_2, [
                            _cache[8] || (_cache[8] = _createElementVNode("h3", { class: "dialog-title" }, "Date range", -1)),
                            _createElementVNode("button", {
                                class: "close-btn",
                                onClick: _cache[0] || (_cache[0] = ($event) => (emit('update:modelValue', false)))
                            }, [
                                _createVNode(_component_v_icon, { size: "18" }, {
                                    default: _withCtx(() => _cache[7] || (_cache[7] = [
                                        _createTextVNode("mdi-close")
                                    ])),
                                    _: 1
                                })
                            ])
                        ]),
                        _createElementVNode("div", _hoisted_3, [
                            _createElementVNode("div", _hoisted_4, [
                                _createElementVNode("div", {
                                    class: "mode-active-bg",
                                    style: _normalizeStyle({ left: mode.value === 'months' ? '2px' : '50%', width: 'calc(50% - 2px)' })
                                }, null, 4),
                                _createElementVNode("button", {
                                    class: _normalizeClass(['mode-option', { selected: mode.value === 'months' }]),
                                    onClick: _cache[1] || (_cache[1] = ($event) => (mode.value = 'months'))
                                }, "Months", 2),
                                _createElementVNode("button", {
                                    class: _normalizeClass(['mode-option', { selected: mode.value === 'years' }]),
                                    onClick: _cache[2] || (_cache[2] = ($event) => (mode.value = 'years'))
                                }, "Years", 2)
                            ]),
                            (mode.value === 'months')
                                ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableYears.value, (year) => {
                                        return (_openBlock(), _createElementBlock("div", {
                                            key: year,
                                            class: "year-group"
                                        }, [
                                            _createElementVNode("div", _hoisted_6, _toDisplayString(year), 1),
                                            _createElementVNode("div", _hoisted_7, [
                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getMonthsForYear(year), (month) => {
                                                    return (_openBlock(), _createElementBlock("button", {
                                                        key: month.key,
                                                        class: _normalizeClass(['month-cell', { selected: isMonthInRange(month.key) }]),
                                                        onClick: ($event) => (toggleMonth(month.key))
                                                    }, _toDisplayString(month.label), 11, _hoisted_8));
                                                }), 128))
                                            ])
                                        ]));
                                    }), 128))
                                ]))
                                : _createCommentVNode("", true),
                            (mode.value === 'years')
                                ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(yearList.value, (year) => {
                                        return (_openBlock(), _createElementBlock("button", {
                                            key: year.key,
                                            class: _normalizeClass(['year-row', { selected: selectedYear.value === year.key }]),
                                            onClick: ($event) => (selectYear(year.key))
                                        }, [
                                            _createElementVNode("span", _hoisted_11, _toDisplayString(year.label), 1),
                                            (selectedYear.value === year.key)
                                                ? (_openBlock(), _createElementBlock("span", {
                                                    key: 0,
                                                    class: "year-deselect",
                                                    onClick: _cache[3] || (_cache[3] = _withModifiers(($event) => (selectedYear.value = null), ["stop"]))
                                                }, [
                                                    _createVNode(_component_v_icon, {
                                                        size: "12",
                                                        color: "white"
                                                    }, {
                                                        default: _withCtx(() => _cache[9] || (_cache[9] = [
                                                            _createTextVNode("mdi-close")
                                                        ])),
                                                        _: 1
                                                    })
                                                ]))
                                                : _createCommentVNode("", true)
                                        ], 10, _hoisted_10));
                                    }), 128))
                                ]))
                                : _createCommentVNode("", true)
                        ]),
                        _createElementVNode("div", _hoisted_12, [
                            (mode.value === 'months' && fromLabel.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                                    _createElementVNode("div", _hoisted_14, [
                                        _cache[11] || (_cache[11] = _createElementVNode("div", { class: "date-field-label" }, "From", -1)),
                                        _createElementVNode("div", {
                                            class: _normalizeClass(['date-field-value', { placeholder: !fromLabel.value }])
                                        }, _toDisplayString(fromLabel.value || 'Select'), 3),
                                        (fromLabel.value)
                                            ? (_openBlock(), _createElementBlock("button", {
                                                key: 0,
                                                class: "date-field-clear",
                                                onClick: clearFrom
                                            }, [
                                                _createVNode(_component_v_icon, {
                                                    size: "8",
                                                    color: "white"
                                                }, {
                                                    default: _withCtx(() => _cache[10] || (_cache[10] = [
                                                        _createTextVNode("mdi-close")
                                                    ])),
                                                    _: 1
                                                })
                                            ]))
                                            : _createCommentVNode("", true)
                                    ]),
                                    _createElementVNode("div", _hoisted_15, [
                                        _cache[13] || (_cache[13] = _createElementVNode("div", { class: "date-field-label" }, "To", -1)),
                                        _createElementVNode("div", {
                                            class: _normalizeClass(['date-field-value', { placeholder: !toLabel.value }])
                                        }, _toDisplayString(toLabel.value || 'Select'), 3),
                                        (toLabel.value)
                                            ? (_openBlock(), _createElementBlock("button", {
                                                key: 0,
                                                class: "date-field-clear",
                                                onClick: clearTo
                                            }, [
                                                _createVNode(_component_v_icon, {
                                                    size: "8",
                                                    color: "white"
                                                }, {
                                                    default: _withCtx(() => _cache[12] || (_cache[12] = [
                                                        _createTextVNode("mdi-close")
                                                    ])),
                                                    _: 1
                                                })
                                            ]))
                                            : _createCommentVNode("", true)
                                    ])
                                ]))
                                : _createCommentVNode("", true),
                            (mode.value === 'years')
                                ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                                    _createElementVNode("div", {
                                        class: "year-type-active-bg",
                                        style: _normalizeStyle({ left: yearType.value === 'calendar' ? '2px' : '50%', width: 'calc(50% - 2px)' })
                                    }, null, 4),
                                    _createElementVNode("button", {
                                        class: _normalizeClass(['year-type-option', { selected: yearType.value === 'calendar' }]),
                                        onClick: _cache[4] || (_cache[4] = ($event) => (yearType.value = 'calendar'))
                                    }, "Calendar year", 2),
                                    _createElementVNode("button", {
                                        class: _normalizeClass(['year-type-option', { selected: yearType.value === 'tax' }]),
                                        onClick: _cache[5] || (_cache[5] = ($event) => (yearType.value = 'tax'))
                                    }, "Tax year", 2)
                                ]))
                                : _createCommentVNode("", true),
                            _createElementVNode("button", {
                                class: "select-btn",
                                disabled: !hasSelection.value,
                                onClick: confirmSelection
                            }, " Select ", 8, _hoisted_17)
                        ])
                    ])
                ]),
                _: 1
            }, 8, ["model-value"]));
        };
    }
});
